import { HttpClient } from '@angular/common/http';
import { Component, inject, signal, Signal, WritableSignal } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import {
  ApiHttpResponse,
  Coordinate,
  TrainingSection,
  VideoSection,
} from '@kinomap-dev/ng-ui-lib/core';
import { ScenicPlayerComponent } from '@kinomap-dev/ng-ui-lib/player';
import { ToasterService } from '@kinomap-dev/ng-ui-lib/elements';
import { map, Subscription } from 'rxjs';
import { environment } from '../environments/environment';
import { UnitService, UnitSystem } from './services/unit.service';
import { SoundService, SoundState } from './services/sound.service';
import { TokenService } from './services/token.service';

@Component({
  selector: 'scenic-player-page',
  standalone: true,
  imports: [ScenicPlayerComponent],
  template: `
    <kin-scenic-player
      [information]="information()"
      [coordinates]="coordinates()"
      [trainingSection]="trainingSection()"
      [unit]="unit()"
      [sounds]="sound()"
      [autoplay]="!isAdmin()"
      [isAuthor]="isAdmin()"
      [isAdmin]="isAdmin()"
      [hasViewOnKinomap]="!isAdmin()"
      [isSyncMode]="isSyncMode()"
      [isSyncLoading]="isSyncLoading()"
      (thumbnailEditClick)="updateThumbnail($event.time)"
      (onSyncClick)="syncClick($event)"
    >
  `,
})
export class ScenicPlayerPage {
  private http: HttpClient = inject(HttpClient);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private unitService: UnitService = inject(UnitService);
  private soundService: SoundService = inject(SoundService);
  private tokenService: TokenService = inject(TokenService);
  private toasterService: ToasterService = inject(ToasterService);
  subcriptions: Subscription[] = [];

  unit: Signal<UnitSystem> = this.unitService.unit;
  sound: Signal<SoundState> = this.soundService.sound;

  information: WritableSignal<VideoSection | undefined> = signal(undefined);
  coordinates: WritableSignal<Coordinate[]> = signal([]);
  trainingSection: WritableSignal<TrainingSection[]> = signal([]);
  redirectUrl: string | undefined = undefined;

  isAdmin: Signal<boolean> = this.tokenService.isAdmin;
  scenicId: WritableSignal<string | undefined> = signal(undefined);
  scenicHashId: WritableSignal<string | undefined> = signal(undefined);
  isSyncMode: WritableSignal<boolean> = signal(false);
  isSyncLoading: WritableSignal<boolean> = signal(false);

  loadVideoSection(scenicId: string): void {
    this.subcriptions.push(
      this.http
        .get<
          ApiHttpResponse<VideoSection>
        >(environment.KINOMAP_API_URL + `/scenic/${scenicId}?appToken=${environment.API_APP_TOKEN}`)
        .subscribe((response: ApiHttpResponse<VideoSection>) => {
          this.scenicHashId.set(response.data.hash_id);
          this.scenicId.set(scenicId);
          this.information.set(response.data);
        }),
    );
  }

  loadCoordinates(scenicId: string): void {
    this.subcriptions.push(
      this.http
        .get<ApiHttpResponse<Coordinate[]>>(
          environment.KINOMAP_API_URL +
          `/scenic/${scenicId}/coordinates?appToken=${environment.API_APP_TOKEN}`,
        )
        .pipe(map((response: ApiHttpResponse<Coordinate[]>) => response.data))
        .subscribe((coordinates: Coordinate[]) => {
          this.coordinates.set(coordinates);
        }),
    );
  }

  loadTrainingSections(scenicId: string): void {
    this.subcriptions.push(
      this.http
        .get<ApiHttpResponse<TrainingSection[]>>(
          environment.KINOMAP_API_URL +
          `/scenic/${scenicId}/training-sections?appToken=${environment.API_APP_TOKEN}`,
        )
        .pipe(map((response: ApiHttpResponse<TrainingSection[]>) => response.data))
        .subscribe((trainingSections: TrainingSection[]) => {
          this.trainingSection.set(trainingSections);
        }),
    );
  }

  loadScenicData(scenicId: string): void {
    this.loadVideoSection(scenicId);
    this.loadCoordinates(scenicId);
    this.loadTrainingSections(scenicId);
  }

  updateThumbnail(
    time: number,
  ): void {
    this.http
      .patch(`${environment.KINOMAP_API_URL}/scenic/{hashId}/updateThumbnail?appToken=${environment.API_APP_TOKEN}`.replace(
        '{hashId}',
        this.scenicHashId()!
      ),
        { time }
      ).subscribe({
        next: () => this.toasterService.success('Miniature', 'La miniature a bien été mise à jour.'),
        error: () => this.toasterService.error('Miniature', 'Erreur lors de la mise à jour de la miniature.'),
      });
  }

  syncClick(event: {
    syncCoordinate: Coordinate,
    videoCoordinate: Coordinate
  }
  ): void {
    if (this.isSyncLoading()) {
      this.toasterService.info('Synchronisation', 'Une synchronisation est déjà en cours.');
      return;
    }

    const startTime: number = event.syncCoordinate?.coo_time - event.videoCoordinate?.position * 1000;

    this.isSyncLoading.set(true);
    this.toasterService.info('Synchronisation', 'Mise à jour de la synchronisation en cours...');
    this.http
      .post(
        `${environment.KINOMAP_API_URL}/scenic/${this.scenicId()}/editSync` +
        `?startTime=${startTime}` +
        `&appToken=${environment.API_APP_TOKEN}`,
        {},
      ).subscribe({
        next: () => {
          this.isSyncMode.set(false);
          this.isSyncLoading.set(false);
          this.toasterService.success('Synchronisation', 'La synchronisation a bien été mise à jour.');
        },
        error: () => {
          this.isSyncLoading.set(false);
          this.toasterService.error('Synchronisation', 'Erreur lors de la mise à jour de la synchronisation.');
        },
      })
  }

  ngOnInit(): void {
    this.subcriptions = [
      ...this.subcriptions,
      this.route.params.subscribe((params: Params) => {
        const scenicId: string = params['scenicId'];
        this.loadScenicData(scenicId);
      }),
    ];
  }

  ngOnDestroy(): void {
    this.subcriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }
}
